/**
 * Settings.tsx — /settings, the person's own settings surface.
 *
 * WHY IT EXISTS AND WHY IT IS THIS SMALL. docs/GUIDED_TOUR.md requires a "Settings → Take the tour
 * again" entry point: the tour tells people it can be reopened from here, so without this page that
 * promise is false and a person who skipped has no way back. This page ships with exactly that one
 * control and nothing else — copy included, so it never implies more is here than is.
 *
 * DEFERRED, DELIBERATELY: the notification opt-outs (NT-F10 kinds 2/3/6/8, NT-F11's "why you cannot
 * turn this one off" for the rest). The API is already there and unused by the web app
 * (GET/PUT /api/me/notification-settings/:kind, routes/me.ts), so the slice drops straight in — but it
 * belongs to the notifications story, needs its own copy for the transactional kinds, and is not
 * something the tour requires. Recorded in docs/technical/IMPLEMENTATION_TRACKER.md rather than
 * half-built here.
 *
 * HOW IT IS REACHED: a "Settings" link on the /me profile page. PRIMARY_NAV is pinned at exactly seven
 * entries by PC-F10 (and vocabulary.ts needs the product owner), so Settings must not be added to it;
 * FR8's profile menu does not exist either. A link in the person's own area is the placement that
 * requires neither.
 *
 * REPLAY MECHANISM: a plain Link to /?tour=1. The tour lives in the frame (App.tsx) and runs over the
 * populated homepage, which is where it is designed to be seen — so the control navigates home and the
 * frame picks up the param, rather than this page holding tour state it would have to hand upwards. A
 * Link, not a button: it is a real destination, and it stays linkable and bookmarkable.
 */
import { useLoaderData } from 'react-router';

import { apiGet } from './community/api';
import { SignedOut } from './community/shared';

export type SettingsLoaderData = { state: 'ok' } | { state: 'signedOut' };

/**
 * Settings is owner-private, so the loader confirms there IS an owner. A 401 renders the sign-in
 * prompt (the same posture as the /me and /notifications loaders); any other non-OK is a real fault.
 * Nothing from the response is read — this page has no per-person data to show yet.
 */
export async function settingsLoader(): Promise<SettingsLoaderData> {
  const res = await apiGet<unknown>('/api/me');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') throw new Error(`GET /api/me failed (${res.status})`);
  return { state: 'ok' };
}

export function Settings() {
  const data = useLoaderData() as SettingsLoaderData;
  if (data.state === 'signedOut') return <SignedOut title="Settings" returnTo="/settings" />;

  return (
    // This page carries its OWN two rules rather than borrowing a slice scope. `.tb-muted` is never
    // defined unscoped — every definition is nested under a slice (`.tb-tools`/`.tb-community`/
    // `.tb-admin`/`.tb-learning`) inside that slice's own inline <style>. So wearing `.tb-community`
    // here would NOT have worked: those rules only exist on pages that also render
    // `<style>{COMMUNITY_STYLES}</style>`, and this page does not (and should not — importing a
    // forum stylesheet to colour one paragraph couples Settings to an unrelated slice, and most of
    // COMMUNITY_STYLES targets classes this page never renders). Same call Notifications.tsx makes.
    <section className="tb-settings" aria-labelledby="settings-heading">
      <style>
        {'.tb-settings { max-width: 60rem; }' +
          ' .tb-settings h1 { margin-top: 0; }' +
          ' .tb-settings a { color: var(--tb-accent); }' +
          ' .tb-settings .tb-muted { color: var(--tb-muted); }'}
      </style>
      <h1 id="settings-heading">Settings</h1>
    </section>
  );
}
